import { useState, useCallback, useEffect, useMemo } from "react";
import { APP_VERSION } from "../config";
import {
    Container,
    Grid,
    Button,
    Stack,
    Typography,
    Alert,
    CircularProgress,
    Box,
    TextField,
    Checkbox,
    FormControlLabel,
    Link,
    Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import SeatSelection from "../components/SeatSelection";
import PassengerForm from "../components/PassengerForm";
import BookingSummary from "../components/BookingSummary";
import TripItinerary from "../components/TripItinerary";
import { setBookingResult, setProcessing, setBookingError } from "../redux/slices/bookingSlice";
import { onlineReserve } from "../api/online";
import { getPricesAPI } from "../api/prices";
import { isPassedDepartureRisk } from "../services/departureRules";
import { getPastDepartureGraceMinutes, DEFAULT_PAST_DEPARTURE_GRACE_MIN } from "../services/runtimeConfig";
import { useAuthContext } from "../auth/FirebaseContext";
import { AUTH } from "../auth/FirebaseContext";

export default function BookingPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { selectedTrip, journeyDate, passengers: passengerCount, tripType } = useSelector((state) => state.search);
    const isRoundTrip = tripType === "return";
    const { isProcessing, error: bookingError } = useSelector((state) => state.booking);
    const { user } = useAuthContext();

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") navigate(-1);
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [navigate]);

    const [selectedSeats, setSelectedSeats] = useState([]);
    const [seatRefreshKey, setSeatRefreshKey] = useState(0);
    const [idempotencyKey] = useState(() => crypto.randomUUID());
    const [passengerDetails, setPassengerDetails] = useState(
        Array.from({ length: passengerCount }, () => ({ ticketType: "" }))
    );
    const [contactEmail, setContactEmail] = useState(user?.email || "");
    const [contactPhone, setContactPhone] = useState("");
    const [emailError, setEmailError] = useState("");
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    // Usputna stanica čije je predviđeno vreme prošlo (luft prozor) — kupac
    // mora izričito da prihvati rizik da je autobus možda već prošao.
    const [acceptedPassedRisk, setAcceptedPassedRisk] = useState(false);
    const [graceMinutes, setGraceMinutes] = useState(DEFAULT_PAST_DEPARTURE_GRACE_MIN);
    useEffect(() => {
        getPastDepartureGraceMinutes().then(setGraceMinutes);
    }, []);
    const passedRisk = isPassedDepartureRisk(selectedTrip, journeyDate, graceMinutes);
    const [availableTicketTypes, setAvailableTicketTypes] = useState([]);
    const [pricesLoading, setPricesLoading] = useState(!!selectedTrip);
    const [pricesError, setPricesError] = useState(null);

    // Kupovina zahteva prijavu — mejl za dostavu se auto-popunjava nalogom
    // (ostaje izmenjiv). Sync radi i ako se korisnik uloguje nakon dolaska.
    useEffect(() => {
        if (user?.email) setContactEmail((prev) => prev || user.email);
    }, [user]);

    useEffect(() => {
        if (!selectedTrip) return;
        const rateCardId = selectedTrip.rateCardId;
        const tripId = selectedTrip.id || selectedTrip.tripId;
        const stationId1 = selectedTrip.departureStationId || selectedTrip.fromStationId;
        const stationId2 = selectedTrip.arrivalStationId || selectedTrip.toStationId;
        const dateOnly = (journeyDate || "").split("T")[0].split(" ")[0];

        if (!rateCardId || !tripId || !stationId1 || !stationId2 || !dateOnly) {
            setPricesError(t("booking.pricesMissingData"));
            setPricesLoading(false);
            return;
        }

        let cancelled = false;
        setPricesLoading(true);
        setPricesError(null);

        getPricesAPI({ rateCardId, stationId1, stationId2, tripId, journeyDate: dateOnly })
            .then((data) => {
                if (cancelled) return;
                const onlineOnly = (data || []).filter((p) => p.onlineSale === true);
                setAvailableTicketTypes(onlineOnly);

                // Za povratnu kartu nude se samo tipovi koji imaju cenu za povratak (priceReturnTicket).
                const forTrip = isRoundTrip
                    ? onlineOnly.filter((p) => typeof p.priceReturnTicket === "number" && p.priceReturnTicket > 0)
                    : onlineOnly;

                // Default tip karte je uvek osnovna karta (ticketTypeId === 1) kad je dostupna.
                const baseTicket = forTrip.find((p) => p.ticketTypeId === 1);
                const defaultId = baseTicket?.ticketTypeId ?? forTrip[0]?.ticketTypeId;
                if (defaultId !== undefined) {
                    setPassengerDetails((prev) =>
                        prev.map((p) => (p.ticketType ? p : { ...p, ticketType: defaultId }))
                    );
                }

                if (isRoundTrip && forTrip.length === 0 && onlineOnly.length > 0) {
                    setPricesError(t("booking.noReturnTicketTypes"));
                } else if (!baseTicket && forTrip.length > 0) {
                    setPricesError(t("booking.baseTicketUnavailable"));
                }
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("getPrices fail", err);
                setPricesError(t("booking.pricesLoadError"));
            })
            .finally(() => {
                if (!cancelled) setPricesLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [selectedTrip, journeyDate, isRoundTrip, t]);

    const handlePassengerChange = useCallback((index, data) => {
        setPassengerDetails((prev) => {
            const updated = [...prev];
            updated[index] = data;
            return updated;
        });
    }, []);

    // Tipovi karata ponuđeni za ovaj polazak. Za povratnu kartu samo oni sa
    // cenom za povratak (priceReturnTicket), inače svi online tipovi.
    const ticketTypesForTrip = useMemo(() => {
        if (!isRoundTrip) return availableTicketTypes;
        return availableTicketTypes.filter(
            (tt) => typeof tt.priceReturnTicket === "number" && tt.priceReturnTicket > 0
        );
    }, [availableTicketTypes, isRoundTrip]);

    // Cena po putniku: za povratnu kartu je to priceReturnTicket (PUNA cena povratne
    // karte — nije doplata na jednosmernu), inače priceOneWay.
    const totalPrice = useMemo(() => {
        return passengerDetails.reduce((sum, p) => {
            const tt = ticketTypesForTrip.find((t) => t.ticketTypeId === p.ticketType);
            if (!tt) return sum;
            const price = isRoundTrip ? (tt.priceReturnTicket || 0) : (tt.priceOneWay || 0);
            return sum + price;
        }, 0);
    }, [passengerDetails, ticketTypesForTrip, isRoundTrip]);

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleEmailBlur = () => {
        if (!contactEmail) {
            setEmailError(t("booking.emailRequired"));
        } else if (!validateEmail(contactEmail)) {
            setEmailError(t("booking.emailInvalid"));
        } else {
            setEmailError("");
        }
    };

    const isFormValid = () => {
        return (
            passengerDetails.every((p) => p.ticketType) &&
            contactEmail &&
            validateEmail(contactEmail) &&
            acceptedTerms &&
            (!passedRisk || acceptedPassedRisk)
        );
    };

    const ensureAuthenticated = async () => {
        // Kupovina je iza AuthGuard-a (pravi nalog), pa korisnik ovde uvek postoji.
        // Gostinski (anonimni) tok se više ne koristi za kupovinu.
        if (AUTH.currentUser) return AUTH.currentUser.uid;
        throw new Error(t("booking.verificationFailed"));
    };

    const handleBooking = async () => {
        if (!isFormValid()) {
            dispatch(setBookingError(t("booking.formInvalid")));
            return;
        }

        dispatch(setProcessing(true));
        dispatch(setBookingError(null));

        try {
            const uid = await ensureAuthenticated();

            const dateOnly = (journeyDate || "").split("T")[0].split(" ")[0];
            const formattedDate = `${dateOnly}T00:00:00`;

            const tripId = selectedTrip.id || selectedTrip.tripId;
            const depStationId = selectedTrip.departureStationId || selectedTrip.fromStationId;
            const arrStationId = selectedTrip.arrivalStationId || selectedTrip.toStationId;

            const tickets = [];
            passengerDetails.forEach((p, i) => {
                const outboundTicket = {
                    trip: { id: tripId },
                    departureStation: { id: depStationId },
                    arrivalStation: { id: arrStationId },
                    ticketType: { id: p.ticketType },
                    journeyDate: formattedDate,
                    seatReserved: selectedSeats[i]?.seatNumber || null,
                    vehicleId: selectedSeats[i]?.vehicleId || null,
                    isReturnTicket: false,
                    isOppositeTicket: false,
                    // Kalem: bez linkId server traži odredište na liniji prve noge
                    // i vrati NOT_ENOUGH_SEATS; sa njim pravi po kartu za svaku nogu.
                    linkId: selectedTrip.linkId ?? null,
                    passenger: {
                        passengerUid: uid,
                        passengerName: "",
                        passengerLastName: "",
                        passengerEmail: contactEmail,
                        passengerTelephone: contactPhone || null,
                        discountId: p.ticketType,
                    },
                };
                tickets.push(outboundTicket);

                if (isRoundTrip) {
                    // Povratni marker bez polaska — linkId ide samo na polazne karte.
                    tickets.push({
                        ...outboundTicket,
                        isReturnTicket: true,
                        trip: { id: null },
                        journeyDate: null,
                        seatReserved: null,
                        vehicleId: null,
                        linkId: null,
                    });
                }
            });

            const amount = totalPrice;

            const payload = {
                idempotencyKey,
                clientVersion: APP_VERSION,
                customer: {
                    name: user?.displayName || contactEmail || "",
                    email: contactEmail,
                    phone: contactPhone || "",
                    document: "",
                    userUid: user?.uid || null,
                },
                amount,
                reservationTtlSeconds: 300,
                presale: {
                    cashier: "bussoft-client",
                    isGroupBooking: false,
                    isTemporary: true,
                    returnDate: null,
                    returnTripId: null,
                    tickets,
                },
                trip: {
                    tripId,
                    journeyDate: formattedDate,
                    depStationId,
                    arrStationId,
                    operatorName: selectedTrip.operatorName || null,
                },
            };

            const reservedAt = Date.now();
            const result = await onlineReserve(payload);
            dispatch(setBookingResult({
                ...result,
                amount,
                customer: payload.customer,
                trip: payload.trip,
                selectedSeats: selectedSeats.map((s) => ({seatNumber: s.seatNumber})),
                reservedAt,
                ttlSeconds: 300,
                // Naziv kasira na fiskalnom računu; transakcija se trasira kroz
                // merchantPaymentId i broj računa, pa fragment ID-a ovde ne treba.
                cashier: "BusGo Online",
            }));
            navigate("/placanje");
        } catch (error) {
            console.error("Greška pri rezervaciji:", error);
            const message = error?.message || error?.data?.message || t("booking.reservationError");
            // „Sedište u međuvremenu zauzeto" — mapa je bajata, ponovo je učitaj
            // da korisnik ne klikće isto (sad tuđe) sedište.
            if (typeof message === "string" && message.toLowerCase().includes("zauzet")) {
                setSeatRefreshKey((k) => k + 1);
            }
            dispatch(setBookingError(message));
        } finally {
            dispatch(setProcessing(false));
        }
    };

    if (!selectedTrip) {
        return (
            <Box sx={{ minHeight: "calc(100vh - 64px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Stack alignItems="center" spacing={2}>
                    <Typography>{t("booking.noTripSelected")}</Typography>
                    <Button variant="contained" onClick={() => navigate("/")}>
                        {t("booking.backToSearch")}
                    </Button>
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "grey.100", py: 4 }}>
            <Container maxWidth="lg">
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            startIcon={<Icon icon="mdi:arrow-left" />}
                            onClick={() => navigate("/rezultati")}
                            sx={{ fontWeight: 600 }}
                        >
                            {t("common.back")}
                        </Button>
                        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                            {t("booking.title")}
                        </Typography>
                    </Stack>
                </Stack>

                {bookingError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {bookingError}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Stack spacing={3}>
                            <TripItinerary trip={selectedTrip} />

                            <SeatSelection
                                trip={selectedTrip}
                                journeyDate={journeyDate}
                                passengers={passengerCount}
                                selectedSeats={selectedSeats}
                                onSeatsChange={setSelectedSeats}
                                refreshKey={seatRefreshKey}
                            />

                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                                {t("booking.passengerDetails")}
                            </Typography>

                            {pricesLoading && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={18} />
                                    <Typography variant="body2" color="text.secondary">
                                        {t("booking.loadingPrices")}
                                    </Typography>
                                </Stack>
                            )}
                            {pricesError && (
                                <Alert severity="warning">{pricesError}</Alert>
                            )}

                            {Array.from({ length: passengerCount }).map((_, index) => (
                                <PassengerForm
                                    key={index}
                                    passengerIndex={index}
                                    totalPassengers={passengerCount}
                                    onChange={handlePassengerChange}
                                    defaultValues={passengerDetails[index]}
                                    ticketTypes={ticketTypesForTrip}
                                    isRoundTrip={isRoundTrip}
                                />
                            ))}

                            {/* Kontakt podaci */}
                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                    <Icon icon="mdi:card-account-details-outline" width={24} color="#1a1a2e" />
                                    <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
                                        {t("booking.contactInfo")}
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t("booking.contactInfoNote")}
                                </Typography>
                                <Stack spacing={2}>
                                    <TextField
                                        fullWidth
                                        label={`${t("booking.contactEmail")} *`}
                                        type="email"
                                        placeholder="vasa.adresa@email.com"
                                        value={contactEmail}
                                        onChange={(e) => {
                                            setContactEmail(e.target.value);
                                            if (emailError) setEmailError("");
                                        }}
                                        onBlur={handleEmailBlur}
                                        error={!!emailError}
                                        helperText={emailError}
                                        InputProps={{
                                            startAdornment: (
                                                <Icon icon="mdi:at" width={20} color="#919EAB" style={{ marginRight: 8 }} />
                                            ),
                                        }}
                                    />
                                    <Stack spacing={0.5}>
                                        <TextField
                                            fullWidth
                                            label={t("booking.contactPhone")}
                                            placeholder="+381 6X XXX XXXX"
                                            value={contactPhone}
                                            onChange={(e) => setContactPhone(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <Icon icon="mdi:phone-outline" width={20} color="#919EAB" style={{ marginRight: 8 }} />
                                                ),
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5 }}>
                                            {t("booking.contactPhoneNote")}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Paper>

                            {/* Predviđeno vreme na usputnoj stanici je prošlo — izričito prihvatanje rizika */}
                            {passedRisk && (
                                <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid", borderColor: "warning.main" }}>
                                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                                        {t("booking.passedDepartureWarning")}
                                    </Alert>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={acceptedPassedRisk}
                                                onChange={(e) => setAcceptedPassedRisk(e.target.checked)}
                                                sx={{ "&.Mui-checked": { color: "warning.main" } }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2">
                                                {t("booking.passedDepartureAccept")}
                                            </Typography>
                                        }
                                    />
                                </Paper>
                            )}

                            {/* Uslovi kupovine */}
                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={acceptedTerms}
                                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                                            sx={{
                                                "&.Mui-checked": { color: "primary.main" },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            {t("booking.acceptTermsPrefix")}{" "}
                                            <Link
                                                href="/uslovi-kupovine"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ fontWeight: 700, color: "primary.main" }}
                                            >
                                                {t("booking.acceptTermsLink")}
                                            </Link>{" "}
                                            {t("booking.acceptTermsAnd")}{" "}
                                            <Link
                                                href="/politika-privatnosti"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ fontWeight: 700, color: "primary.main" }}
                                            >
                                                {t("booking.acceptPrivacyLink")}
                                            </Link>
                                        </Typography>
                                    }
                                />
                            </Paper>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box sx={{ position: "sticky", top: 80 }}>
                            <BookingSummary
                                trip={selectedTrip}
                                passengers={passengerCount}
                                selectedSeats={selectedSeats}
                                totalPrice={totalPrice || null}
                                isRoundTrip={isRoundTrip}
                                loading={pricesLoading}
                            />
                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={handleBooking}
                                disabled={isProcessing || !isFormValid()}
                                startIcon={
                                    isProcessing ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        <Icon icon="mdi:credit-card-check" />
                                    )
                                }
                                sx={{
                                    mt: 2,
                                    py: 1.5,
                                    fontSize: "1.1rem",
                                    fontWeight: 700,
                                    borderRadius: 2,
                                }}
                            >
                                {isProcessing ? t("booking.processing") : t("booking.confirmAndBuy")}
                            </Button>

                            {!acceptedTerms && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 1 }}>
                                    {t("booking.mustAcceptTerms")}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}
